import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, Avatar, IconButton, InputBase, Tooltip, useTheme, Divider, Button, Menu, MenuItem, CircularProgress, Modal,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, FormControlLabel, Switch, TextField, Popover
} from '@mui/material';
import {
  Send as SendIcon, EmojiEmotions as EmojiIcon, MoreVert as MoreVertIcon,
  ArrowBack as ArrowBackIcon, SmartToy as BotIcon, Phone as PhoneIcon,
  Videocam as VideocamIcon, Block as BlockIcon, FlashOn as FlashOnIcon,
  AutoAwesome as IaIcon, AttachFile as AttachFileIcon, Person as PersonIcon,
  Reply as ReplyIcon, NoteAlt as NoteIcon, Schedule as ReminderIcon,
  KeyboardArrowDown as ArrowDownIcon, CheckCircle as AcceptIcon,
  Close as CloseIcon, ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon,
  Download as DownloadIcon, RestartAlt as ResetZoomIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon, InsertDriveFile as DocumentIcon,
  Mic as MicIcon, StopCircle as StopCircleIcon,
  SwapHoriz as TransferIcon, GroupWork as SetorIcon,
  Undo as UndoIcon, Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Collections as StickersIcon,
  ExpandLess as ExpandLessIcon,
  BorderColor as AssinaturaIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { atendimentoService, AtendimentoTicket, AtendimentoMensagem } from '../../../services/atendimentoService';
import TagsMultiSelect from '../../../components/selects/TagsMultiSelect';
import respostaRapidaService from '../../../services/respostaRapidaService';
import atendimentoFigurinhaService from '../../../services/atendimentoFigurinhaService';
import { AtendimentoFigurinha, MensagemRapida } from '../../../types/atendimento';
import SetoresAllSelect from './SetoresAllSelect';
import UsuariosSelect from '../../../components/selects/UsuariosSelect';
import { Socket } from 'socket.io-client';
import { env } from '../../../utils/env';
import { getCountryByNumber } from '../../../utils/countries';
import { usePermissions, useAuth } from '../../../contexts/AuthContext';
import { getMensagemRemetenteLabel } from '../../../utils/usuarioUtils';
import AtendimentoMensagemConteudo from './AtendimentoMensagemConteudo';
import {
  buildReactionsByWid,
  downloadMediaFile,
  formatFileSize,
  getFileNameFromUrl,
  getMensagemPreviewText,
  getQuotedMessagePreviewText,
  getQuotedWidFromMessage,
} from '../utils/mensagemConteudoUtils';
import { exportarChatPdf } from '../utils/exportarChatPdf';
import { useNavigate } from 'react-router-dom';
import ViabilidadeModal from '../../../components/modals/ViabilidadeModal';
import { ftthCaixaService, FtthCaixa } from '../../../services/ftthCaixaService';

const CORES = ['#5c6bc0', '#26a69a', '#ef5350', '#ff7043', '#ab47bc', '#29b6f6', '#66bb6a', '#ffa726'];
const ASSINATURA_STORAGE_KEY = 'atendimento_chat_assinatura_habilitada';
const EMOJIS_POPULARES = [
  '\u{1F600}', '\u{1F603}', '\u{1F604}', '\u{1F601}', '\u{1F606}', '\u{1F605}', '\u{1F602}', '\u{1F923}',
  '\u{1F60A}', '\u{1F607}', '\u{1F642}', '\u{1F643}', '\u{1F609}', '\u{1F60C}', '\u{1F60D}', '\u{1F970}',
  '\u{1F618}', '\u{1F917}', '\u{1F914}', '\u{1F928}', '\u{1F44D}', '\u{1F44E}', '\u{1F44C}', '\u{270C}\u{FE0F}',
  '\u{1F64F}', '\u{1F44F}', '\u{1F64C}', '\u{1F4AA}', '\u{2764}\u{FE0F}', '\u{1F499}', '\u{1F49A}', '\u{1F49B}',
  '\u{1F525}', '\u{1F4AF}', '\u{1F389}', '\u{2728}', '\u{1F44B}', '\u{1F91D}', '\u{1F622}', '\u{1F62D}',
  '\u{1F62E}', '\u{1F631}', '\u{1F914}', '\u{1F634}', '\u{1F60E}', '\u{1F973}', '\u{1F4AC}', '\u{1F4A1}'
];

const getInitials = (name: string) => name ? name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase() : 'AT';

const fmtMsgTime = (iso: string) => new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const toMsgDateKey = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

const formatMsgDateLabel = (iso: string) => {
  const d = new Date(iso);
  const hoje = new Date();
  const ontem = new Date();
  ontem.setDate(ontem.getDate() - 1);

  if (d.toDateString() === hoje.toDateString()) return 'Hoje';
  if (d.toDateString() === ontem.toDateString()) return 'Ontem';

  return d.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const getTicketStatusLabel = (status?: AtendimentoTicket['status'] | null) => {
  switch (status) {
    case 'em_atendimento':
      return 'Em atendimento';
    case 'pendente':
      return 'Aguardando';
    case 'fechado':
      return 'Fechado';
    case 'fechado_bot':
      return 'Fechado (Bot)';
    default:
      return status || 'Atendimento';
  }
};

const formatTicketDate = (value?: string | null) => {
  if (!value) return '';
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

type PreviewImage = {
  src: string;
  name?: string | null;
  caption?: string | null;
};

const getClienteStatus = (atendimento: AtendimentoTicket & { cliente?: { status?: string | null } }) => {
  return atendimento.contato.cliente?.status?.trim().toLowerCase()
    || atendimento.cliente?.status?.trim().toLowerCase()
    || '';
};

const getFigurinhaSrc = (path: string): string => {
  if (path.startsWith('http') || path.startsWith('data:')) return path;
  return `${env.API_URL.replace(/\/$/, '')}${path}`;
};

const isClienteStatusCritico = (status: string) => {
  return ['inativo', 'suspenso', 'bloqueado', 'inadimplente'].includes(status);
};

const getSupportedAudioMimeType = (): string => {
  if (typeof MediaRecorder === 'undefined') return '';

  const options = [
    'audio/ogg; codecs=opus',
    'audio/webm; codecs=opus',
    'audio/webm',
    'audio/mp4'
  ];

  return options.find((type) => MediaRecorder.isTypeSupported(type)) || '';
};

const getAudioExtension = (mimeType: string): string => {
  const cleanMime = mimeType.split(';')[0];
  if (cleanMime === 'audio/ogg') return 'ogg';
  if (cleanMime === 'audio/mp4') return 'm4a';
  if (cleanMime === 'audio/mpeg') return 'mp3';
  if (cleanMime === 'audio/wav') return 'wav';
  return 'webm';
};

const formatRecordingTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};

export interface ChatColunaProps {
  atendimento: AtendimentoTicket;
  onUpdate: (updated: AtendimentoTicket) => void;
  mostrarDetalhes: boolean;
  isMobile: boolean;
  onVoltar: () => void;
  onToggleDetalhes: () => void;
  socket: Socket | null;
}

const ChatColuna: React.FC<ChatColunaProps> = ({ atendimento, onUpdate, mostrarDetalhes, isMobile, onVoltar, onToggleDetalhes, socket }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can } = usePermissions();
  const podeExcluirTicket = can('atendimento.excluir_ticket');
  const podeDevolverTicket = can('atendimento.devolver_ticket');
  const podeAdicionarFigurinha = can('atendimento.adicionar_figurinha');
  const podeUsarIa = can('atendimento.chat.use_ia');
  const podeDesabilitarMsgAssumirTicket = can('atendimento.chat.desabilitar_msg_assumir_ticket');
  const podeDesabilitarMsgFinalizarTicket = can('atendimento.chat.desabilitar_msg_finalizar_ticket');
  const podeDesabilitarAssinatura = can('atendimento.chat.desabilitar_assinatura');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesExportRef = useRef<HTMLDivElement>(null);
  const chatHeaderRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<number | null>(null);
  const cancelRecordingRef = useRef(false);
  const mensagemInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const [mensagens, setMensagens] = useState<AtendimentoMensagem[]>([]);
  const [loading, setLoading] = useState(true);
  const [temHistoricoAnterior, setTemHistoricoAnterior] = useState(false);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [ticketMaisAntigoId, setTicketMaisAntigoId] = useState<number>(atendimento.id);
  const [ticketsCarregados, setTicketsCarregados] = useState<Map<number, AtendimentoTicket>>(new Map());
  const [mensagem, setMensagem] = useState('');
  const [gravandoAudio, setGravandoAudio] = useState(false);
  const [enviandoAudio, setEnviandoAudio] = useState(false);
  const [tempoGravacao, setTempoGravacao] = useState(0);
  const [abaReply, setAbaReply] = useState<'reply' | 'note' | 'reminder' | 'shortcuts'>('reply');
  const [assinaturaHabilitada, setAssinaturaHabilitada] = useState(() => {
    try {
      const stored = localStorage.getItem(ASSINATURA_STORAGE_KEY);
      if (stored === null) return true;
      return stored === 'true';
    } catch {
      return true;
    }
  });
  const [anchorStatus, setAnchorStatus] = useState<null | HTMLElement>(null);
  const [previewImage, setPreviewImage] = useState<PreviewImage | null>(null);
  const [anchorEmoji, setAnchorEmoji] = useState<null | HTMLElement>(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [anchorMensagem, setAnchorMensagem] = useState<null | HTMLElement>(null);
  const [mensagemSelecionada, setMensagemSelecionada] = useState<AtendimentoMensagem | null>(null);
  const [respostaMensagem, setRespostaMensagem] = useState<AtendimentoMensagem | null>(null);
  const [editandoMensagem, setEditandoMensagem] = useState<AtendimentoMensagem | null>(null);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [sendFarewellMessage, setSendFarewellMessage] = useState(true);
  const [closingTicket, setClosingTicket] = useState(false);
  const [deleteTicketDialogOpen, setDeleteTicketDialogOpen] = useState(false);
  const [deletingTicket, setDeletingTicket] = useState(false);
  const [assumeDialogOpen, setAssumeDialogOpen] = useState(false);
  const [assumingTicket, setAssumingTicket] = useState(false);
  const [assumeSetorId, setAssumeSetorId] = useState<number | null>(null);
  const [sendAssumeMessage, setSendAssumeMessage] = useState(true);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferindoTicket, setTransferindoTicket] = useState(false);
  const [transferTipo, setTransferTipo] = useState<'usuario' | 'setor'>('usuario');
  const [transferUsuarioId, setTransferUsuarioId] = useState<number | null>(null);
  const [transferSetorId, setTransferSetorId] = useState<number | null>(null);
  const [transferMensagemPrivada, setTransferMensagemPrivada] = useState('');
  const [sendTransferMessage, setSendTransferMessage] = useState(true);
  const [respostasRapidas, setRespostasRapidas] = useState<MensagemRapida[]>([]);
  const [loadingRespostasRapidas, setLoadingRespostasRapidas] = useState(false);
  const [showRespostasRapidas, setShowRespostasRapidas] = useState(false);
  const [figurinhas, setFigurinhas] = useState<AtendimentoFigurinha[]>([]);
  const [loadingFigurinhas, setLoadingFigurinhas] = useState(false);
  const [enviandoFigurinhaId, setEnviandoFigurinhaId] = useState<number | null>(null);
  const [anchorFigurinhas, setAnchorFigurinhas] = useState<null | HTMLElement>(null);
  const [dialogCadastroFigurinhaOpen, setDialogCadastroFigurinhaOpen] = useState(false);
  const [novoStickerAtivo, setNovoStickerAtivo] = useState(true);
  const [novoStickerCompartilhar, setNovoStickerCompartilhar] = useState(true);
  const [cadastrandoSticker, setCadastrandoSticker] = useState(false);
  const [exportandoChatPdf, setExportandoChatPdf] = useState(false);
  const [gerandoIa, setGerandoIa] = useState(false);
  const [arquivoColado, setArquivoColado] = useState<File | null>(null);
  const [previewArquivoUrl, setPreviewArquivoUrl] = useState<string | null>(null);
  const [legendaArquivoColado, setLegendaArquivoColado] = useState('');
  const [enviandoArquivoColado, setEnviandoArquivoColado] = useState(false);
  const [modalFotoAberto, setModalFotoAberto] = useState(false);
  const [viabilidadeModalOpen, setViabilidadeModalOpen] = useState(false);
  const [viabilidadeCoords, setViabilidadeCoords] = useState<{ lat: string; lng: string } | null>(null);
  const [viabilidadeCaixas, setViabilidadeCaixas] = useState<FtthCaixa[]>([]);
  const [loadingViabilidadeCaixas, setLoadingViabilidadeCaixas] = useState(false);
  const podeResponder = atendimento.status === 'em_atendimento';
  const atendimentoPendente = atendimento.status === 'pendente';

  const marcarComoLido = async () => {
    try {
      const ticket = await atendimentoService.marcarTicketComoLido(atendimento.id);
      onUpdate(ticket);
    } catch (err) {
      console.error('Erro ao marcar atendimento como lido', err);
    }
  };

  const handleConsultarViabilidade = useCallback(async (coords: { lat: number; lng: number }) => {
    setViabilidadeCoords({
      lat: coords.lat.toFixed(7),
      lng: coords.lng.toFixed(7),
    });
    setViabilidadeModalOpen(true);

    if (viabilidadeCaixas.length > 0) return;

    setLoadingViabilidadeCaixas(true);
    try {
      const response = await ftthCaixaService.getCaixas({ limit: 5000, tipo: 'cto' });
      const list = (response as any)?.data?.caixas ?? (response as any)?.caixas ?? [];
      setViabilidadeCaixas(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Erro ao carregar caixas para viabilidade', err);
      toast.error('Erro ao carregar caixas para consulta de viabilidade.');
    } finally {
      setLoadingViabilidadeCaixas(false);
    }
  }, [viabilidadeCaixas.length]);

  const handleMostrarViabilidadeNoMapa = useCallback((resultados: { id: number }[]) => {
    navigate('/ftth', {
      state: {
        ftthTab: 4,
        viabilidade: {
          lat: viabilidadeCoords?.lat,
          lng: viabilidadeCoords?.lng,
          caixaIds: resultados.map((c) => c.id),
        },
      },
    });
  }, [navigate, viabilidadeCoords]);

  useEffect(() => {
    setTicketMaisAntigoId(atendimento.id);
    setTemHistoricoAnterior(false);
    setTicketsCarregados(new Map([[atendimento.id, atendimento]]));
    carregarMensagens();
    marcarComoLido();

    if (socket) {
      const msgHandler = (payload: AtendimentoMensagem | { mensagem?: AtendimentoMensagem }) => {
        const msg = ((payload as any).mensagem || payload) as AtendimentoMensagem;
        if (!msg) return;

        if (msg.ticket_id === atendimento.id) {
          shouldAutoScrollRef.current = true;
          setMensagens(prev => [...prev, msg]);
          if (!msg.de_mim) marcarComoLido();
        }
      };

      const ackHandler = (data: { msgId: number, ack: string, wid: string }) => {
        setMensagens(prev => prev.map(m => m.wid === data.wid ? { ...m, ack: data.ack as any } : m));
      };

      const updateHandler = (payload: AtendimentoMensagem | { mensagem?: AtendimentoMensagem }) => {
        const msg = ((payload as any).mensagem || payload) as AtendimentoMensagem;
        if (!msg || msg.ticket_id !== atendimento.id) return;
        setMensagens(prev => prev.map(m => m.id === msg.id ? { ...m, ...msg } : m));
      };

      socket.on('atendimento:nova_mensagem', msgHandler);
      socket.on('atendimento:message_ack', ackHandler);
      socket.on('atendimento:mensagem_update', updateHandler);

      return () => {
        socket.off('atendimento:nova_mensagem', msgHandler);
        socket.off('atendimento:message_ack', ackHandler);
        socket.off('atendimento:mensagem_update', updateHandler);
      };
    }
  }, [atendimento.id, socket]);

  useEffect(() => {
    const handlePreencherMensagem = (event: CustomEvent<{ texto: string; ticketId?: number }>) => {
      const { texto, ticketId } = event.detail || {};
      if (ticketId && ticketId !== atendimento.id) return;
      if (texto) {
        setMensagem(texto);
        setAbaReply('reply');
        setTimeout(() => {
          mensagemInputRef.current?.focus();
        }, 100);
      }
    };

    window.addEventListener('sysup:preencher-mensagem-chat', handlePreencherMensagem as EventListener);
    return () => {
      window.removeEventListener('sysup:preencher-mensagem-chat', handlePreencherMensagem as EventListener);
    };
  }, [atendimento.id]);

  useEffect(() => {
    if (shouldAutoScrollRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      shouldAutoScrollRef.current = false;
    }
  }, [mensagens]);

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        window.clearInterval(recordingTimerRef.current);
      }
      if (mediaRecorderRef.current?.state === 'recording') {
        cancelRecordingRef.current = true;
        mediaRecorderRef.current.stop();
      }
      audioStreamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const carregarMensagens = async () => {
    try {
      setLoading(true);
      const msgs = await atendimentoService.getMensagens(atendimento.id);
      setMensagens(msgs);
      shouldAutoScrollRef.current = true;

      const possuiAnterior = await atendimentoService.verificarHistoricoAnterior(atendimento.id).catch(() => false);
      setTemHistoricoAnterior(possuiAnterior);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const carregarHistoricoAnterior = async () => {
    if (carregandoHistorico || !temHistoricoAnterior) return;

    const container = messagesContainerRef.current;
    const scrollHeightBefore = container?.scrollHeight ?? 0;
    const scrollTopBefore = container?.scrollTop ?? 0;

    try {
      setCarregandoHistorico(true);
      const data = await atendimentoService.getHistoricoAnterior(ticketMaisAntigoId);

      if (!data.ticket) {
        setTemHistoricoAnterior(false);
        return;
      }

      setTicketMaisAntigoId(data.ticket.id);
      setTemHistoricoAnterior(Boolean(data.possui_mais));
      setTicketsCarregados((prev) => {
        const next = new Map(prev);
        next.set(data.ticket!.id, data.ticket!);
        return next;
      });

      if (!data.mensagens?.length) {
        return;
      }

      setMensagens((prev) => [...data.mensagens, ...prev]);

      requestAnimationFrame(() => {
        if (!container) return;
        const scrollHeightAfter = container.scrollHeight;
        container.scrollTop = scrollTopBefore + (scrollHeightAfter - scrollHeightBefore);
      });
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.error || 'Erro ao carregar mensagens anteriores.');
    } finally {
      setCarregandoHistorico(false);
    }
  };

  useEffect(() => {
    const isShortcutSearch = mensagem.startsWith('/') && !editandoMensagem && !gravandoAudio && !enviandoAudio && podeResponder;

    if (!isShortcutSearch) {
      setShowRespostasRapidas(false);
      setRespostasRapidas([]);
      return;
    }

    setShowRespostasRapidas(true);
    const search = mensagem.slice(1).trim();
    const timer = window.setTimeout(async () => {
      try {
        setLoadingRespostasRapidas(true);
        const response = await respostaRapidaService.getAll({
          page: 1,
          limit: 20,
          search: search || undefined,
          searchAtalhoOnly: true,
          activeOnly: true,
        });
        setRespostasRapidas(response.success ? (response.data || []) : []);
      } catch (error) {
        setRespostasRapidas([]);
      } finally {
        setLoadingRespostasRapidas(false);
      }
    }, 180);

    return () => window.clearTimeout(timer);
  }, [mensagem, editandoMensagem, gravandoAudio, enviandoAudio, podeResponder]);

  const carregarFigurinhas = async () => {
    try {
      setLoadingFigurinhas(true);
      const response = await atendimentoFigurinhaService.getAll({
        page: 1,
        limit: 48,
        activeOnly: true,
      });
      setFigurinhas(response.success ? (response.data || []) : []);
    } catch (error) {
      console.error('Erro ao carregar figurinhas', error);
      setFigurinhas([]);
    } finally {
      setLoadingFigurinhas(false);
    }
  };

  const handleAbrirFigurinhas = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorFigurinhas(event.currentTarget);
    carregarFigurinhas();
  };

  const handleFecharFigurinhas = () => {
    setAnchorFigurinhas(null);
  };

  const handleEnviarFigurinha = async (figurinha: AtendimentoFigurinha) => {
    if (atendimento.status !== 'em_atendimento') {
      toast.warning('Aceite o atendimento antes de enviar mensagens.');
      return;
    }

    try {
      setEnviandoFigurinhaId(figurinha.id);
      await atendimentoFigurinhaService.send(atendimento.id, figurinha.id);
      setRespostaMensagem(null);
      handleFecharFigurinhas();
    } catch (err: any) {
      console.error('Erro ao enviar figurinha', err);
      toast.error(err?.response?.data?.message || 'Erro ao enviar figurinha.');
    } finally {
      setEnviandoFigurinhaId(null);
    }
  };

  const handleSelecionarRespostaRapida = (resposta: MensagemRapida) => {
    setMensagem(resposta.mensagem);
    setShowRespostasRapidas(false);
    setRespostasRapidas([]);
  };

  const handleMensagemChange = (value: string) => {
    setMensagem(value);
    if (!value.startsWith('/')) {
      setShowRespostasRapidas(false);
    }
  };

  const handleAbrirEmojis = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEmoji(event.currentTarget);
  };

  const handleFecharEmojis = () => {
    setAnchorEmoji(null);
  };

  const handleSelecionarEmoji = (emoji: string) => {
    const input = mensagemInputRef.current;
    const start = input?.selectionStart ?? mensagem.length;
    const end = input?.selectionEnd ?? mensagem.length;
    const proximaMensagem = `${mensagem.slice(0, start)}${emoji}${mensagem.slice(end)}`;

    setMensagem(proximaMensagem);
    setAnchorEmoji(null);

    window.setTimeout(() => {
      const nextPosition = start + emoji.length;
      input?.focus();
      input?.setSelectionRange(nextPosition, nextPosition);
    }, 0);
  };

  const handleMelhorarComIa = async () => {
    const texto = mensagem.trim();
    if (!texto) {
      toast.warning('Digite uma mensagem antes de usar a IA.');
      return;
    }

    if (!podeUsarIa) {
      toast.warning('Você não tem permissão para usar IA no chat.');
      return;
    }

    if (!podeResponder) {
      toast.warning('Aceite o atendimento antes de usar a IA.');
      return;
    }

    const token = localStorage.getItem('sysup_token');
    if (!token) {
      toast.error('Sessão expirada. Faça login novamente.');
      return;
    }

    setGerandoIa(true);

    try {
      const prompt = `Você é um assistente especializado em criar mensagens para WhatsApp no contexto de atendimento ao cliente. Com base no texto abaixo, gere uma mensagem clara, objetiva e amigável. Retorne apenas o texto da mensagem, sem explicações adicionais.

Texto:
${texto}`;

      const response = await fetch(`${env.API_URL}/api/openai/chat/generate-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt,
          maxTokens: 500,
          temperature: 0.7,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.code === 'OPENAI_NOT_CONFIGURED') {
          toast.error('Serviço de IA não configurado. Configure o token da OpenAI nas configurações.');
        } else {
          toast.error(errorData.error || 'Erro ao gerar texto com IA.');
        }
        return;
      }

      const data = await response.json();
      const generatedText = (data.text || '').trim();

      if (!generatedText) {
        toast.error('Nenhum texto foi gerado. Tente novamente.');
        return;
      }

      setMensagem(generatedText);
      window.setTimeout(() => mensagemInputRef.current?.focus(), 0);
    } catch (err) {
      console.error('Erro ao gerar texto com IA', err);
      toast.error('Erro inesperado. Verifique sua conexão e tente novamente.');
    } finally {
      setGerandoIa(false);
    }
  };

  const handleMensagemKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (showRespostasRapidas && e.key === 'Escape') {
      e.preventDefault();
      setShowRespostasRapidas(false);
      return;
    }

    if (showRespostasRapidas && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (respostasRapidas.length > 0) {
        handleSelecionarRespostaRapida(respostasRapidas[0]);
      }
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEnviar();
    }
  };

  const handleEnviar = async () => {
    if (atendimento.status !== 'em_atendimento') {
      toast.warning('Aceite o atendimento antes de enviar mensagens.');
      return;
    }
    if (!mensagem.trim()) return;
    const txt = mensagem;
    const tipoMensagem = abaReply === 'note' ? 'private' : 'text';
    const usarAssinatura = (podeDesabilitarAssinatura ? assinaturaHabilitada : true) && tipoMensagem === 'text';
    setMensagem('');
    try {
      if (editandoMensagem) {
        const mensagemAtualizada = await atendimentoService.updateMensagem(
          editandoMensagem.id,
          txt,
          usarAssinatura,
        );
        setMensagens(prev => prev.map(m => m.id === mensagemAtualizada.id ? { ...m, ...mensagemAtualizada } : m));
        setEditandoMensagem(null);
        toast.success('Mensagem editada.');
        return;
      }

      await atendimentoService.sendMensagem(
        atendimento.id,
        txt,
        tipoMensagem,
        tipoMensagem === 'text' ? respostaMensagem?.wid : null,
        usarAssinatura,
      );
      setRespostaMensagem(null);
    } catch (err: any) {
      setMensagem(txt);
      console.error("Erro ao enviar msg", err);
      toast.error(err?.response?.data?.error || err?.response?.data?.message || (editandoMensagem ? 'Erro ao editar mensagem.' : tipoMensagem === 'private' ? 'Erro ao salvar nota privada.' : 'Erro ao enviar mensagem.'));
    }
  };

  const handleEnviarArquivo = async (file: File, caption?: string) => {
    if (atendimento.status !== 'em_atendimento') {
      toast.warning('Aceite o atendimento antes de enviar mensagens.');
      return false;
    }

    try {
      const usarAssinatura = (podeDesabilitarAssinatura ? assinaturaHabilitada : true) && Boolean(caption?.trim());
      await atendimentoService.sendMedia(atendimento.id, file, caption, usarAssinatura);
      setRespostaMensagem(null);
      return true;
    } catch (err: any) {
      console.error('Erro ao enviar arquivo', err);
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Erro ao enviar arquivo.');
      return false;
    }
  };

  const limparGravacaoAudio = () => {
    if (recordingTimerRef.current) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    audioStreamRef.current?.getTracks().forEach((track) => track.stop());
    audioStreamRef.current = null;
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    setGravandoAudio(false);
    setTempoGravacao(0);
  };

  const handleIniciarGravacaoAudio = async () => {
    if (atendimento.status !== 'em_atendimento') {
      toast.warning('Aceite o atendimento antes de enviar mensagens.');
      return;
    }

    if (editandoMensagem || abaReply === 'note') {
      toast.warning('Áudio só pode ser enviado como resposta ao cliente.');
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('Gravação de áudio não é suportada neste navegador.');
      return;
    }

    try {
      cancelRecordingRef.current = false;
      audioChunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedAudioMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      audioStreamRef.current = stream;
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        toast.error('Erro ao gravar áudio.');
        limparGravacaoAudio();
      };

      recorder.onstop = async () => {
        const shouldCancel = cancelRecordingRef.current;
        const chunks = [...audioChunksRef.current];
        const recordedMimeType = recorder.mimeType || chunks[0]?.type || mimeType || 'audio/webm';

        limparGravacaoAudio();
        cancelRecordingRef.current = false;

        if (shouldCancel) return;
        if (!chunks.length) {
          toast.error('Nenhum áudio foi gravado.');
          return;
        }

        try {
          setEnviandoAudio(true);
          const blob = new Blob(chunks, { type: recordedMimeType });
          const extension = getAudioExtension(recordedMimeType);
          const file = new File([blob], `audio-atendimento-${Date.now()}.${extension}`, { type: recordedMimeType });
          await handleEnviarArquivo(file);
        } finally {
          setEnviandoAudio(false);
        }
      };

      recorder.start();
      setGravandoAudio(true);
      setTempoGravacao(0);
      recordingTimerRef.current = window.setInterval(() => {
        setTempoGravacao((value) => value + 1);
      }, 1000);
    } catch (err: any) {
      limparGravacaoAudio();
      const errorName = err?.name || '';
      toast.error(errorName === 'NotAllowedError' ? 'Permissão do microfone negada.' : 'Erro ao acessar o microfone.');
    }
  };

  const handlePararGravacaoAudio = (cancelar = false) => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      limparGravacaoAudio();
      return;
    }

    cancelRecordingRef.current = cancelar;
    recorder.stop();
  };

  const handleSelecionarArquivo = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const enviado = await handleEnviarArquivo(file, mensagem);
    if (enviado) setMensagem('');
  };

  const abrirPreviewArquivoColado = (file: File) => {
    setArquivoColado(file);
    setLegendaArquivoColado(mensagem);

    if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPreviewArquivoUrl(ev.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else if (file.type.startsWith('audio/')) {
      setPreviewArquivoUrl(URL.createObjectURL(file));
    } else {
      setPreviewArquivoUrl(null);
    }
  };

  const handleFecharPreviewArquivoColado = () => {
    if (previewArquivoUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(previewArquivoUrl);
    }
    setArquivoColado(null);
    setPreviewArquivoUrl(null);
    setLegendaArquivoColado('');
    setEnviandoArquivoColado(false);
  };

  const handleEnviarArquivoColado = async () => {
    if (!arquivoColado) return;

    setEnviandoArquivoColado(true);
    try {
      const enviado = await handleEnviarArquivo(arquivoColado, legendaArquivoColado || undefined);
      if (enviado) {
        setMensagem('');
        handleFecharPreviewArquivoColado();
      }
    } finally {
      setEnviandoArquivoColado(false);
    }
  };

  const handleInputPaste = (e: React.ClipboardEvent) => {
    if (!podeResponder || gravandoAudio || enviandoAudio || gerandoIa) return;

    if (e.clipboardData.files[0]) {
      e.preventDefault();
      abrirPreviewArquivoColado(e.clipboardData.files[0]);
      return;
    }

    if (e.clipboardData?.items) {
      for (let i = 0; i < e.clipboardData.items.length; i++) {
        const item = e.clipboardData.items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            abrirPreviewArquivoColado(file);
            return;
          }
        }
      }
    }
  };

  const abrirDetalhesContato = () => {
    setAnchorStatus(null);
    if (!mostrarDetalhes) {
      onToggleDetalhes();
    }
  };

  // [Atendimento] Fecha chat local quando outro atendente assume o ticket (resposta 409).
  const fecharChatPorAssuncao = (ticket?: AtendimentoTicket) => {
    if (ticket) {
      onUpdate(ticket);
    }
    onVoltar();
  };

  const handleMudarStatus = async (novoStatus: string) => {
    setAnchorStatus(null);
    try {
      if (novoStatus === 'em_atendimento') {
        // Botao "Assumir" so aparece em pendente; bloqueio extra para estado desatualizado.
        if (atendimento.status !== 'pendente') {
          toast.info(`Este atendimento já está sendo realizado por ${atendimento.usuario?.nome || 'outro atendente'}.`);
          return;
        }
        setAssumeSetorId(atendimento.setor_id || atendimento.setor?.id || null);
        setSendAssumeMessage(true);
        setAssumeDialogOpen(true);
      } else if (novoStatus === 'fechado') {
        setSendFarewellMessage(true);
        setCloseDialogOpen(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDevolverAtendimento = async () => {
    setAnchorStatus(null);

    const result = await Swal.fire({
      title: 'Devolver atendimento',
      text: 'O atendimento voltará para a fila de aguardando. Deseja continuar?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Devolver',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ed6c02',
      cancelButtonColor: '#6b7280',
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      const t = await atendimentoService.devolverTicket(atendimento.id);
      onUpdate(t);
      toast.success('Atendimento devolvido para a fila.');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Erro ao devolver atendimento.');
    }
  };

  const handleConfirmarAssumirTicket = async () => {
    const ticketTemSetor = Boolean(atendimento.setor_id || atendimento.setor?.id);

    if (!ticketTemSetor && !assumeSetorId) {
      toast.warning('Selecione um setor para assumir o atendimento.');
      return;
    }

    try {
      setAssumingTicket(true);
      const t = await atendimentoService.assumirTicket(atendimento.id, {
        setor_id: ticketTemSetor ? undefined : assumeSetorId,
        ...(podeDesabilitarMsgAssumirTicket ? { sendAssumeMessage } : {}),
      });
      onUpdate(t);
      setAssumeDialogOpen(false);
    } catch (err: any) {
      console.error(err);
      const status = err?.response?.status;
      const ticketAtualizado = err?.response?.data?.ticket as AtendimentoTicket | undefined;
      const mensagem = err?.response?.data?.error || err?.response?.data?.message;

      if (status === 409) {
        // Ticket assumido por outro atendente enquanto este usuario tentava assumir.
        toast.info(mensagem || 'Este atendimento já está sendo realizado por outro atendente.');
        fecharChatPorAssuncao(ticketAtualizado);
        return;
      }

      toast.error(mensagem || 'Erro ao assumir atendimento.');
    } finally {
      setAssumingTicket(false);
    }
  };

  const abrirTransferencia = () => {
    setAnchorStatus(null);
    setTransferTipo('usuario');
    setTransferUsuarioId(null);
    setTransferSetorId(atendimento.setor_id || atendimento.setor?.id || null);
    setTransferMensagemPrivada('');
    setSendTransferMessage(true);
    setTransferDialogOpen(true);
  };

  const handleConfirmarTransferencia = async () => {
    if (transferTipo === 'usuario' && !transferUsuarioId) {
      toast.warning('Selecione o usuario de destino.');
      return;
    }

    if (transferTipo === 'setor' && !transferSetorId) {
      toast.warning('Selecione o setor de destino.');
      return;
    }

    try {
      setTransferindoTicket(true);
      const t = await atendimentoService.transferirTicket(atendimento.id, {
        usuario_id: transferTipo === 'usuario' ? transferUsuarioId : null,
        setor_id: transferTipo === 'setor' ? transferSetorId : null,
        private_message: transferMensagemPrivada.trim() || undefined,
        send_transfer_message: sendTransferMessage,
      });
      onUpdate(t);
      setTransferDialogOpen(false);
      toast.success('Atendimento transferido com sucesso.');
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Erro ao transferir atendimento.');
    } finally {
      setTransferindoTicket(false);
    }
  };

  const handleConfirmarEncerramento = async () => {
    try {
      setClosingTicket(true);
      const t = await atendimentoService.finalizarTicket(atendimento.id, {
        ...(podeDesabilitarMsgFinalizarTicket ? { sendFarewellMessage } : {}),
      });
      onUpdate(t);
      setCloseDialogOpen(false);
      setSendFarewellMessage(true);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao finalizar atendimento.');
    } finally {
      setClosingTicket(false);
    }
  };

  const abrirExclusaoTicket = () => {
    setAnchorStatus(null);
    setDeleteTicketDialogOpen(true);
  };

  const handleConfirmarExclusaoTicket = async () => {
    try {
      setDeletingTicket(true);
      await atendimentoService.deleteTicket(atendimento.id);
      setDeleteTicketDialogOpen(false);
      toast.success('Atendimento excluido permanentemente.');
      onVoltar();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Erro ao excluir atendimento.');
    } finally {
      setDeletingTicket(false);
    }
  };

  const handleAbrirImagem = (image: PreviewImage) => {
    setPreviewImage(image);
    setPreviewZoom(1);
  };

  const handleFecharImagem = () => {
    setPreviewImage(null);
    setPreviewZoom(1);
  };

  const handleDownloadImagem = async () => {
    if (!previewImage) return;

    const filename = previewImage.name || getFileNameFromUrl(previewImage.src) || 'imagem-atendimento';
    await downloadMediaFile(previewImage.src, filename);
  };

  const abrirMenuMensagem = (event: React.MouseEvent<HTMLElement>, msg: AtendimentoMensagem) => {
    event.stopPropagation();
    setAnchorMensagem(event.currentTarget);
    setMensagemSelecionada(msg);
  };

  const fecharMenuMensagem = () => {
    setAnchorMensagem(null);
    setMensagemSelecionada(null);
  };

  const handleResponderMensagem = () => {
    if (mensagemSelecionada?.deletada) return;
    setRespostaMensagem(mensagemSelecionada);
    setEditandoMensagem(null);
    setAbaReply('reply');
    fecharMenuMensagem();
  };

  const handleEditarMensagem = () => {
    if (!mensagemSelecionada || mensagemSelecionada.tipo !== 'text' || mensagemSelecionada.deletada) return;
    setEditandoMensagem(mensagemSelecionada);
    setRespostaMensagem(null);
    setAbaReply('reply');
    setMensagem(mensagemSelecionada.corpo || '');
    fecharMenuMensagem();
  };

  const handleAbrirCadastroFigurinha = () => {
    setAnchorMensagem(null);
    setNovoStickerAtivo(true);
    setNovoStickerCompartilhar(true);
    setDialogCadastroFigurinhaOpen(true);
  };

  const handleFecharCadastroFigurinha = () => {
    setDialogCadastroFigurinhaOpen(false);
    setMensagemSelecionada(null);
  };

  const handleConfirmarCadastroFigurinha = async () => {
    if (!mensagemSelecionada?.media_url) return;

    try {
      setCadastrandoSticker(true);
      const response = await atendimentoFigurinhaService.create({
        media_url: mensagemSelecionada.media_url,
        ativo: novoStickerAtivo,
        compartilhar: novoStickerCompartilhar
      });

      if (response.success) {
        toast.success('Figurinha cadastrada com sucesso!');
        carregarFigurinhas();
      } else {
        toast.error(response.message || 'Erro ao cadastrar figurinha.');
      }
    } catch (error: any) {
      console.error('Erro ao cadastrar figurinha:', error);
      toast.error(error?.response?.data?.message || 'Erro ao cadastrar figurinha.');
    } finally {
      setCadastrandoSticker(false);
      handleFecharCadastroFigurinha();
    }
  };

  const handleDeletarMensagem = async () => {
    const msg = mensagemSelecionada;
    fecharMenuMensagem();
    if (!msg) return;

    const result = await Swal.fire({
      title: 'Deletar mensagem',
      text: 'Tem certeza que deseja deletar esta mensagem?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Deletar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#6b7280',
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      const mensagemAtualizada = await atendimentoService.deleteMensagem(msg.id);
      setMensagens(prev => prev.map(m => m.id === mensagemAtualizada.id ? { ...m, ...mensagemAtualizada } : m));
      if (respostaMensagem?.id === msg.id) setRespostaMensagem(null);
      if (editandoMensagem?.id === msg.id) {
        setEditandoMensagem(null);
        setMensagem('');
      }
      toast.success('Mensagem deletada.');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Erro ao deletar mensagem.');
    }
  };

  const handleToggleAssinatura = () => {
    setAssinaturaHabilitada((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(ASSINATURA_STORAGE_KEY, String(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  };

  const statusLabel =
    atendimento.status === 'em_atendimento' ? 'Em atendimento' :
      atendimento.status === 'pendente' ? 'Aguardando' :
        atendimento.status === 'fechado' ? 'Fechado' : 'Bot';

  const statusColor =
    atendimento.status === 'em_atendimento' ? 'warning' :
      atendimento.status === 'pendente' ? 'error' :
        atendimento.status === 'fechado' ? 'success' : 'info';
  const abas = [
    { key: 'reply', label: 'Responder', icon: <ReplyIcon sx={{ fontSize: 13 }} /> },
    { key: 'note', label: 'Nota', icon: <NoteIcon sx={{ fontSize: 13 }} /> },
  ];

  const corAvatar = CORES[atendimento.contato_id % CORES.length];
  const statusCliente = getClienteStatus(atendimento);
  const mostrarStatusCliente = Boolean(atendimento.contato.cliente_id && isClienteStatusCritico(statusCliente));
  const isDark = theme.palette.mode === 'dark';
  const chatBackground = isDark ? theme.palette.background.default : '#f0f2f5';
  const mineBubbleBg = alpha(theme.palette.primary.main, 0.9);
  const incomingBubbleBg = isDark ? alpha(theme.palette.common.white, 0.08) : '#fff';
  const privateBubbleBg = isDark ? alpha(theme.palette.warning.main, 0.16) : '#fff7d6';
  const privateBorder = isDark ? alpha(theme.palette.warning.light, 0.55) : '#d39b13';
  const privateAccent = isDark ? theme.palette.warning.light : '#d39b13';
  const privateLabelColor = isDark ? theme.palette.warning.light : '#8a5d00';
  const bubbleShadow = isDark ? '0 1px 2px rgba(0,0,0,0.28)' : '0 1px 2px rgba(0,0,0,0.08)';
  const privateShadow = isDark ? '0 1px 3px rgba(0,0,0,0.32)' : '0 1px 3px rgba(143, 101, 0, 0.16)';

  const getQuotedMessageStyles = (isMineMessage: boolean) => {
    if (isMineMessage) {
      return {
        box: {
          mb: 0.75,
          px: 1,
          py: 0.6,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.common.white, isDark ? 0.14 : 0.18),
          borderLeft: `3px solid ${alpha(theme.palette.common.white, 0.75)}`,
          maxWidth: { xs: 230, sm: 310 },
        },
        nameColor: alpha(theme.palette.common.white, 0.96),
        previewColor: alpha(theme.palette.common.white, 0.84),
      };
    }

    return {
      box: {
        mb: 0.75,
        px: 1,
        py: 0.6,
        borderRadius: 1,
        bgcolor: isDark
          ? alpha(theme.palette.common.white, 0.1)
          : alpha(theme.palette.primary.main, 0.1),
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        maxWidth: { xs: 230, sm: 310 },
      },
      nameColor: theme.palette.primary.main,
      previewColor: isDark
        ? alpha(theme.palette.common.white, 0.74)
        : theme.palette.text.secondary,
    };
  };

  const inicioPorTicket = useMemo(() => {
    const map = new Map<number, number>();
    mensagens.forEach((msg, index) => {
      if (!map.has(msg.ticket_id)) map.set(msg.ticket_id, index);
    });
    return map;
  }, [mensagens]);
  const indicesSeparadorData = useMemo(() => {
    const set = new Set<number>();
    let ultimaData: string | null = null;

    mensagens.forEach((msg, index) => {
      if (msg.tipo === 'reaction') return;
      const dataKey = toMsgDateKey(msg.timestamp);
      if (ultimaData === null || dataKey !== ultimaData) {
        set.add(index);
      }
      ultimaData = dataKey;
    });

    return set;
  }, [mensagens]);
  const possuiMultiplosTickets = useMemo(
    () => new Set(mensagens.map((msg) => msg.ticket_id)).size > 1,
    [mensagens]
  );
  const usuariosPorId = useMemo(() => {
    const map = new Map<number, { nome?: string; nome_resumido?: string | null }>();
    if (user) {
      map.set(user.id, { nome: user.nome, nome_resumido: user.nome_resumido });
    }
    mensagens.forEach((msg) => {
      if (msg.usuario_id && msg.usuario) {
        map.set(msg.usuario_id, msg.usuario);
      }
    });
    return map;
  }, [user, mensagens]);
  const contatoNome = atendimento.contato?.nome || 'Contato';
  const obterRemetenteLabel = (msg: AtendimentoMensagem) =>
    getMensagemRemetenteLabel(msg, contatoNome, user?.id, usuariosPorId);
  const obterTicketInfo = (ticketId: number): AtendimentoTicket | null => {
    if (ticketId === atendimento.id) return atendimento;
    return ticketsCarregados.get(ticketId) || null;
  };

  const handleExportarChatPdf = async () => {
    setAnchorStatus(null);

    const mensagensVisiveis = mensagens.filter((msg) => msg.tipo !== 'reaction');
    if (!mensagensVisiveis.length) {
      toast.warning('Nenhuma mensagem para exportar.');
      return;
    }

    if (!messagesExportRef.current) {
      toast.error('Não foi possível capturar o conteúdo do chat.');
      return;
    }

    const scrollAnterior = messagesContainerRef.current?.scrollTop ?? 0;
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = 0;
    }

    try {
      setExportandoChatPdf(true);
      await exportarChatPdf({
        elementoMensagens: messagesExportRef.current,
        elementoCabecalho: chatHeaderRef.current,
        atendimento,
        contatoNome,
        chatBackground,
      });
      toast.success('PDF exportado com sucesso.');
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao exportar PDF.');
    } finally {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = scrollAnterior;
      }
      setExportandoChatPdf(false);
    }
  };

  const reactionsByWid = buildReactionsByWid(mensagens);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', bgcolor: chatBackground, minWidth: 0 }}>
      {/* Header */}
      <Box ref={chatHeaderRef} sx={{ px: 2, py: 1, bgcolor: 'background.paper', borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <Tooltip title="Fechar conversa">
          <IconButton size="small" onClick={onVoltar} data-export-ignore><ArrowBackIcon sx={{ fontSize: 18 }} /></IconButton>
        </Tooltip>

        <Avatar
          src={atendimentoService.getAvatarUrl(atendimento.contato.foto_perfil)}
          onClick={() => {
            if (atendimento.contato.foto_perfil) {
              setModalFotoAberto(true);
            }
          }}
          sx={{
            width: 36,
            height: 36,
            bgcolor: corAvatar,
            cursor: atendimento.contato.foto_perfil ? 'pointer' : 'default',
          }}
        >
          {!atendimento.contato.foto_perfil && getInitials(atendimento.contato.nome)}
        </Avatar>
        <Box
          onClick={abrirDetalhesContato}
          sx={{ cursor: 'pointer', '&:hover .MuiTypography-root': { color: 'primary.main' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {atendimento.contato.cliente_id && (
              <Tooltip title="Contato vinculado a um cliente">
                <CheckCircleIcon sx={{ fontSize: 14, color: '#2e7d32', flexShrink: 0 }} />
              </Tooltip>
            )}
            {mostrarStatusCliente && (
              <Tooltip title={`Atenção: Cliente está com status ${statusCliente.toUpperCase()}`}>
                <WarningIcon
                  sx={{
                    fontSize: 14,
                    color: statusCliente === 'inativo'
                      ? '#9e9e9e'
                      : statusCliente === 'bloqueado'
                        ? '#ed6c02'
                        : '#d32f2f',
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}
            <Typography variant="subtitle2" fontWeight={600} lineHeight={1.2}>#{atendimento.id} - {atendimento.contato.nome}</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            {(() => {
              const country = getCountryByNumber(atendimento.contato.ddi || atendimento.contato.numero);
              return country ? (
                <Tooltip title={country.label}>
                  <img
                    loading="lazy"
                    width="14"
                    src={new URL(`../../../assets/countries/w20/${country.code.toLowerCase()}.png`, import.meta.url).href}
                    alt={country.label}
                    style={{ borderRadius: 2 }}
                  />
                </Tooltip>
              ) : null;
            })()}
            <Typography variant="caption" color="text.secondary">{atendimento.contato.numero}</Typography>
          </Box>
        </Box>

        <Box flex={1} />

        <Button size="small" variant="contained" color={statusColor} endIcon={<ArrowDownIcon sx={{ fontSize: 14 }} />} onClick={(e) => setAnchorStatus(e.currentTarget)} sx={{ fontSize: '0.75rem', textTransform: 'none', borderRadius: 2, px: 1.5, py: 0.4, fontWeight: 600 }}>
          {statusLabel}
        </Button>
      </Box>

      {/* Barra de Tags */}
      <Box
        sx={{
          px: 0,
          py: 0,
          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.4) : alpha('#f5f5f7', 0.8),
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <TagsMultiSelect
          value={atendimento.tags || []}
          onChange={async (novasTags) => {
            try {
              const t = await atendimentoService.updateTicket(atendimento.id, { tags: novasTags });
              onUpdate(t);
            } catch (err) {
              console.error('Erro ao atualizar tags:', err);
            }
          }}
          label=""
          placeholder="Vincular tags a este ticket..."
          size="small"
        />
      </Box>

      {/* Mensagens */}
      <Box ref={messagesContainerRef} sx={{ flex: 1, overflowY: 'auto', px: { xs: 1.5, sm: 3 }, py: 2, display: 'flex', flexDirection: 'column', gap: 0.8, '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: theme.palette.divider, borderRadius: 2 } }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}><CircularProgress size={30} /></Box>
        ) : (
          <>
            {temHistoricoAnterior && (
              <Box display="flex" justifyContent="center" mb={0.5} data-export-ignore>
                <Button
                  size="small"
                  variant="text"
                  disabled={carregandoHistorico}
                  onClick={carregarHistoricoAnterior}
                  startIcon={carregandoHistorico ? <CircularProgress size={14} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    borderRadius: 999,
                    px: 1.5,
                    py: 0.35,
                    color: 'primary.main',
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.14) },
                  }}
                >
                  {carregandoHistorico ? 'Carregando...' : 'Ver mais'}
                </Button>
              </Box>
            )}
            <Box ref={messagesExportRef} sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              {mensagens.map((msg, index) => {
                const isMine = msg.de_mim;
                const isPrivate = msg.tipo === 'private';
                const isBot = false; // futuramente podemos checar se foi de bot
                const isStickerMessage = msg.tipo === 'sticker';
                if (msg.tipo === 'reaction') return null;

                const attachedReactions = reactionsByWid.get(msg.wid) || [];
                const quotedWid = getQuotedWidFromMessage(msg);
                const quotedMessage = quotedWid
                  ? mensagens.find((item) => item.wid === quotedWid)
                  : msg.citacao_id
                    ? mensagens.find((item) => item.id === msg.citacao_id)
                    : null;
                const hasQuotedMessage = Boolean(quotedWid || msg.citacao_id || quotedMessage);
                const bubbleBg = isPrivate ? privateBubbleBg : isMine ? mineBubbleBg : incomingBubbleBg;
                const bubbleBorder = isPrivate ? `1px dashed ${privateBorder}` : isDark ? `1px solid ${alpha(theme.palette.common.white, 0.08)}` : 'none';
                const bubbleBorderLeft = isPrivate ? `4px solid ${privateAccent}` : bubbleBorder;
                const bubblePaddingX = isStickerMessage ? 0.4 : 1.5;
                const bubblePaddingY = isStickerMessage ? 0.35 : 0.9;
                const quotedStyles = getQuotedMessageStyles(isMine);
                const mostrarAcoesMensagem = !isPrivate && !msg.deletada;
                const indiceInicioTicket = inicioPorTicket.get(msg.ticket_id);
                const mostrarSeparadorTicket = possuiMultiplosTickets && indiceInicioTicket === index;
                const ticketSeparador = mostrarSeparadorTicket ? obterTicketInfo(msg.ticket_id) : null;
                const mostrarSeparadorData = indicesSeparadorData.has(index);

                return (
                  <React.Fragment key={msg.id}>
                    {mostrarSeparadorData && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          my: 0.5,
                          px: 1,
                        }}
                      >
                        <Divider sx={{ flex: 1 }} />
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            color: 'text.secondary',
                            textAlign: 'center',
                            px: 0.5,
                            lineHeight: 1.45,
                            textTransform: 'capitalize',
                          }}
                        >
                          {formatMsgDateLabel(msg.timestamp)}
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                    )}
                    {mostrarSeparadorTicket && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          my: 0.5,
                          px: 1,
                        }}
                      >
                        <Divider sx={{ flex: 1 }} />
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            color: 'text.secondary',
                            textAlign: 'center',
                            px: 0.5,
                            lineHeight: 1.45,
                          }}
                        >
                          Atendimento #{msg.ticket_id}
                          {ticketSeparador ? ` • ${getTicketStatusLabel(ticketSeparador.status)}` : ''}
                          {ticketSeparador?.createdAt ? ` • Abertura: ${formatTicketDate(ticketSeparador.createdAt)}` : ''}
                          {ticketSeparador?.data_fechamento
                            ? ` • Fechamento: ${formatTicketDate(ticketSeparador.data_fechamento)}`
                            : ticketSeparador
                              ? ' • Em aberto'
                              : ''}
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                    )}
                    <Box display="flex" flexDirection={isPrivate || isMine ? 'row-reverse' : 'row'} alignItems="flex-end" gap={0.8}>
                      <Box display="flex" flexDirection="column" alignItems={isPrivate ? 'flex-end' : isMine ? 'flex-end' : 'flex-start'} sx={{ maxWidth: isPrivate ? { xs: '94%', sm: '78%' } : { xs: '85%', sm: '70%' } }}>
                        <Box
                          sx={{
                            position: 'relative',
                            bgcolor: isStickerMessage ? 'transparent' : bubbleBg,
                            color: isMine && !isPrivate ? '#fff' : 'text.primary',
                            border: isStickerMessage ? 'none' : bubbleBorder,
                            borderLeft: isStickerMessage ? 'none' : bubbleBorderLeft,
                            borderRadius: isPrivate ? '8px' : isMine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                            px: bubblePaddingX,
                            py: bubblePaddingY,
                            boxShadow: isStickerMessage ? 'none' : isPrivate ? privateShadow : bubbleShadow,
                            '&:hover .message-actions-button': {
                              opacity: mostrarAcoesMensagem ? 1 : 0,
                              pointerEvents: mostrarAcoesMensagem ? 'auto' : 'none'
                            }
                          }}
                        >
                          {mostrarAcoesMensagem && (
                            <Tooltip title="Opcoes da mensagem">
                              <IconButton
                                size="small"
                                className="message-actions-button"
                                onClick={(event) => abrirMenuMensagem(event, msg)}
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  width: 24,
                                  height: 24,
                                  opacity: anchorMensagem && mensagemSelecionada?.id === msg.id ? 1 : 0,
                                  pointerEvents: anchorMensagem && mensagemSelecionada?.id === msg.id ? 'auto' : 'none',
                                  transition: 'opacity 0.12s ease',
                                  bgcolor: alpha(theme.palette.background.paper, 0.92),
                                  color: 'text.secondary',
                                  zIndex: 3,
                                  '&:hover': {
                                    bgcolor: theme.palette.background.paper,
                                    color: 'text.primary'
                                  }
                                }}
                              >
                                <MoreVertIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {attachedReactions.length > 0 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: -12,
                                [isMine || isPrivate ? 'right' : 'left']: 10,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.2,
                                px: 0.55,
                                py: 0.1,
                                minHeight: 22,
                                borderRadius: 999,
                                bgcolor: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.divider}`,
                                boxShadow: bubbleShadow,
                                zIndex: 2,
                                fontSize: '0.9rem',
                                lineHeight: 1
                              }}
                            >
                              {attachedReactions.slice(-3).map((item, index) => (
                                <Box component="span" key={`${item.quotedWid}-${index}`}>
                                  {item.emoji}
                                </Box>
                              ))}
                            </Box>
                          )}
                          {isPrivate && (
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                              <NoteIcon sx={{ fontSize: 13, color: privateAccent }} />
                              <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.62rem', color: privateLabelColor, textTransform: 'uppercase' }}>
                                Nota privada
                              </Typography>
                            </Box>
                          )}
                          {!isPrivate && (
                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: isMine && !isPrivate ? '#fff' : theme.palette.primary.main, mb: 0.5 }}>
                              {obterRemetenteLabel(msg)}:
                            </Typography>
                          )}
                          {hasQuotedMessage && (
                            <Box sx={quotedStyles.box}>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  fontSize: '0.66rem',
                                  fontWeight: 700,
                                  color: quotedStyles.nameColor,
                                }}
                              >
                                {quotedMessage ? obterRemetenteLabel(quotedMessage) : contatoNome}
                              </Typography>
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{
                                  display: 'block',
                                  fontSize: '0.7rem',
                                  color: quotedStyles.previewColor,
                                }}
                              >
                                {getQuotedMessagePreviewText(msg, quotedMessage || null)}
                              </Typography>
                            </Box>
                          )}
                          {msg.deletada && (
                            <Box
                              sx={{
                                mb: 0.75,
                                px: 0.9,
                                py: 0.45,
                                borderRadius: 1,
                                bgcolor: alpha(theme.palette.error.main, isDark ? 0.18 : 0.08),
                                color: 'error.main',
                                border: `1px solid ${alpha(theme.palette.error.main, isDark ? 0.35 : 0.18)}`
                              }}
                            >
                              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.68rem', fontWeight: 700 }}>
                                Mensagem apagada no WhatsApp
                              </Typography>
                            </Box>
                          )}
                          <AtendimentoMensagemConteudo
                            msg={msg}
                            isMine={isMine}
                            isPrivate={isPrivate}
                            onOpenImage={handleAbrirImagem}
                            onConsultarViabilidade={handleConsultarViabilidade}
                          />
                          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.4} mt={0.3} mb={attachedReactions.length > 0 ? 1.15 : 0}>
                            {msg.editada && !msg.deletada && (
                              <Typography variant="caption" sx={{ fontSize: '0.62rem', color: isMine && !isPrivate ? 'rgba(255, 255, 255, 0.7)' : 'text.disabled' }}>editada</Typography>
                            )}
                            <Typography variant="caption" sx={{ fontSize: '0.62rem', color: isMine && !isPrivate ? 'rgba(255, 255, 255, 0.7)' : 'text.disabled' }}>{fmtMsgTime(msg.timestamp)}</Typography>
                            {isMine && !isPrivate && (
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, color: msg.ack === 'read' ? '#58eb53ff' : 'rgba(255, 255, 255, 0.7)' }}>
                                {msg.ack === 'pending' ? '🕒' : msg.ack === 'sent' ? '✓' : '✓✓'}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>
          </>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box sx={{ bgcolor: 'background.paper', borderTop: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
        {atendimentoPendente && (
          <Box sx={{ px: 1.5, py: 2, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.08)' : '#fff8e1', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="contained" color="success" startIcon={<AcceptIcon sx={{ fontSize: 16 }} />} onClick={() => handleMudarStatus('em_atendimento')} sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.75rem', fontWeight: 700 }}>
              Assumir atendimento
            </Button>
          </Box>
        )}
        {!atendimentoPendente && (
          <>
            <Box display="flex" px={1} pt={0.5} sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
              {abas.map((ab) => (
                <Box key={ab.key} onClick={() => podeResponder && setAbaReply(ab.key as any)} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 1.2, py: 0.6, cursor: podeResponder ? 'pointer' : 'not-allowed', fontSize: '0.75rem', fontWeight: abaReply === ab.key ? 600 : 400, color: !podeResponder ? 'text.disabled' : abaReply === ab.key ? 'primary.main' : 'text.secondary', borderBottom: abaReply === ab.key ? `2px solid ${podeResponder ? theme.palette.primary.main : theme.palette.action.disabled}` : '2px solid transparent', transition: 'all 0.15s', '&:hover': { color: podeResponder ? 'primary.main' : 'text.disabled' } }}>
                  {ab.icon}{ab.label}
                </Box>
              ))}
            </Box>
            <Box sx={{ px: 1.5, py: 1 }}>
              {(respostaMensagem || editandoMensagem) && (
                <Box
                  sx={{
                    mb: 0.8,
                    px: 1,
                    py: 0.75,
                    borderRadius: 1.5,
                    bgcolor: alpha(editandoMensagem ? theme.palette.warning.main : theme.palette.primary.main, isDark ? 0.18 : 0.08),
                    borderLeft: `3px solid ${editandoMensagem ? theme.palette.warning.main : theme.palette.primary.main}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  {editandoMensagem ? <EditIcon sx={{ fontSize: 16, color: 'warning.main' }} /> : <ReplyIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
                  <Box minWidth={0} flex={1}>
                    <Typography variant="caption" sx={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: editandoMensagem ? 'warning.main' : 'primary.main' }}>
                      {editandoMensagem ? 'Editando mensagem' : 'Respondendo mensagem'}
                    </Typography>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        display: 'block',
                        fontSize: '0.72rem',
                        color: theme.palette.text.primary,
                        opacity: 0.78,
                      }}
                    >
                      {getMensagemPreviewText(editandoMensagem || respostaMensagem)}
                    </Typography>
                  </Box>
                  <Tooltip title="Cancelar">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setRespostaMensagem(null);
                        setEditandoMensagem(null);
                        if (editandoMensagem) setMensagem('');
                      }}
                      sx={{ width: 26, height: 26 }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={handleSelecionarArquivo}
              />
              {gravandoAudio && (
                <Box
                  sx={{
                    mb: 0.8,
                    px: 1,
                    py: 0.75,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.error.main, isDark ? 0.18 : 0.08),
                    color: 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.8
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                  <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.76rem' }}>
                    Gravando áudio {formatRecordingTime(tempoGravacao)}
                  </Typography>
                </Box>
              )}
              <InputBase inputRef={mensagemInputRef} fullWidth multiline maxRows={4} disabled={!podeResponder || gravandoAudio || enviandoAudio || gerandoIa} placeholder={!podeResponder ? 'Aceite o atendimento para responder...' : editandoMensagem ? 'Edite sua mensagem...' : abaReply === 'note' ? 'Digite uma nota privada...' : gravandoAudio ? 'Gravando áudio...' : gerandoIa ? 'Gerando com IA...' : 'Digite uma mensagem...'} value={mensagem} onChange={(e) => handleMensagemChange(e.target.value)} onKeyDown={handleMensagemKeyDown} onPaste={handleInputPaste} sx={{ fontSize: '0.875rem', mb: 0.5, lineHeight: 1.5 }} />

              {showRespostasRapidas && (
                <Box
                  sx={{
                    mb: 0.8,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                    bgcolor: 'background.paper',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                    maxHeight: 240,
                    overflowY: 'auto',
                  }}
                >
                  <Box sx={{ px: 1, py: 0.65, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 0.7 }}>
                    <FlashOnIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      Respostas rapidas
                    </Typography>
                    {loadingRespostasRapidas && <CircularProgress size={12} sx={{ ml: 'auto' }} />}
                  </Box>
                  {!loadingRespostasRapidas && respostasRapidas.length === 0 ? (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', px: 1, py: 1 }}>
                      Nenhuma resposta encontrada.
                    </Typography>
                  ) : (
                    respostasRapidas.map((resposta) => (
                      <Box
                        key={resposta.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelecionarRespostaRapida(resposta);
                        }}
                        sx={{
                          px: 1,
                          py: 0.75,
                          cursor: 'pointer',
                          borderTop: `1px solid ${theme.palette.divider}`,
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={0.8}>
                          <Typography variant="caption" fontWeight={800} color="primary.main" sx={{ fontFamily: 'monospace' }}>
                            {resposta.atalho}
                          </Typography>
                          {resposta.showAllUsers && (
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                              todos
                            </Typography>
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.35,
                          }}
                        >
                          {resposta.mensagem}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              )}
              <Box display="flex" alignItems="center" gap={0.5}>
                <Tooltip title="Anexar arquivo"><span><IconButton size="small" disabled={!podeResponder || gravandoAudio || enviandoAudio} onClick={() => fileInputRef.current?.click()}><AttachFileIcon sx={{ fontSize: 17, color: podeResponder && !gravandoAudio && !enviandoAudio ? 'text.secondary' : 'text.disabled' }} /></IconButton></span></Tooltip>
                {podeDesabilitarAssinatura && (
                  <Tooltip title={assinaturaHabilitada ? 'Assinatura ativada' : 'Assinatura desativada'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!podeResponder || gravandoAudio || enviandoAudio || abaReply === 'note'}
                        onClick={handleToggleAssinatura}
                        sx={{
                          bgcolor: assinaturaHabilitada && abaReply !== 'note' ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                        }}
                      >
                        <AssinaturaIcon
                          sx={{
                            fontSize: 17,
                            color: assinaturaHabilitada && podeResponder && !gravandoAudio && !enviandoAudio && abaReply !== 'note'
                              ? 'primary.main'
                              : 'text.disabled',
                          }}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title="Emoji"><span><IconButton size="small" disabled={!podeResponder || gravandoAudio || enviandoAudio} onClick={handleAbrirEmojis}><EmojiIcon sx={{ fontSize: 17, color: podeResponder && !gravandoAudio && !enviandoAudio ? 'text.secondary' : 'text.disabled' }} /></IconButton></span></Tooltip>
                <Tooltip title="Figurinhas"><span><IconButton size="small" disabled={!podeResponder || gravandoAudio || enviandoAudio || Boolean(editandoMensagem) || abaReply === 'note'} onClick={handleAbrirFigurinhas}><StickersIcon sx={{ fontSize: 17, color: podeResponder && !gravandoAudio && !enviandoAudio && !editandoMensagem && abaReply !== 'note' ? 'text.secondary' : 'text.disabled' }} /></IconButton></span></Tooltip>
                {podeUsarIa && (
                  <Tooltip title="Melhorar com IA">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!podeResponder || gravandoAudio || enviandoAudio || gerandoIa || !mensagem.trim()}
                        onClick={handleMelhorarComIa}
                      >
                        {gerandoIa ? (
                          <CircularProgress size={15} />
                        ) : (
                          <IaIcon sx={{ fontSize: 17, color: podeResponder && !gravandoAudio && !enviandoAudio && mensagem.trim() ? 'secondary.main' : 'text.disabled' }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {gravandoAudio ? (
                  <>
                    <Tooltip title="Cancelar gravação">
                      <span>
                        <IconButton size="small" color="inherit" onClick={() => handlePararGravacaoAudio(true)}>
                          <CloseIcon sx={{ fontSize: 17, color: 'error.main' }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Parar e enviar áudio">
                      <span>
                        <IconButton size="small" color="error" onClick={() => handlePararGravacaoAudio(false)}>
                          <StopCircleIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                ) : (
                  <Tooltip title="Gravar áudio">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!podeResponder || enviandoAudio || Boolean(mensagem.trim()) || Boolean(editandoMensagem) || abaReply === 'note'}
                        onClick={handleIniciarGravacaoAudio}
                      >
                        {enviandoAudio ? (
                          <CircularProgress size={15} />
                        ) : (
                          <MicIcon sx={{ fontSize: 18, color: podeResponder && !mensagem.trim() && !editandoMensagem && abaReply !== 'note' ? 'text.secondary' : 'text.disabled' }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Box flex={1} />
                <IconButton onClick={handleEnviar} disabled={!podeResponder || gravandoAudio || enviandoAudio || !mensagem.trim()} sx={{ bgcolor: podeResponder && !gravandoAudio && !enviandoAudio && mensagem.trim() ? 'primary.main' : 'action.disabledBackground', color: podeResponder && !gravandoAudio && !enviandoAudio && mensagem.trim() ? '#fff' : 'text.disabled', '&:hover': { bgcolor: podeResponder && !gravandoAudio && !enviandoAudio && mensagem.trim() ? 'primary.dark' : 'action.disabledBackground' }, width: 32, height: 32, borderRadius: 2 }}>
                  {editandoMensagem ? <EditIcon sx={{ fontSize: 16 }} /> : <SendIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* Menu Status */}
      <Menu anchorEl={anchorStatus} open={Boolean(anchorStatus)} onClose={() => setAnchorStatus(null)} PaperProps={{ sx: { borderRadius: 2, minWidth: 160 } }}>
        <MenuItem onClick={abrirDetalhesContato} sx={{ fontSize: '0.8rem' }}>
          <PersonIcon sx={{ fontSize: 16, mr: 1 }} />
          Ver contato
        </MenuItem>
        <Divider />
        {atendimento.status === 'pendente' && (
          <MenuItem onClick={() => handleMudarStatus('em_atendimento')} sx={{ fontSize: '0.8rem', color: 'success.main', fontWeight: 500 }}>
            <CheckIcon sx={{ fontSize: 16, mr: 1 }} />
            Assumir atendimento
          </MenuItem>
        )}
        {atendimento.status === 'em_atendimento' && podeDevolverTicket && (
          <MenuItem onClick={handleDevolverAtendimento} sx={{ fontSize: '0.8rem', color: 'warning.main' }}>
            <UndoIcon sx={{ fontSize: 16, mr: 1 }} />
            Devolver Atendimento
          </MenuItem>
        )}
        {atendimento.status !== 'fechado' && atendimento.status !== 'fechado_bot' && (
          <MenuItem onClick={abrirTransferencia} sx={{ fontSize: '0.8rem' }}>
            <TransferIcon sx={{ fontSize: 16, mr: 1 }} />
            Transferir Atendimento
          </MenuItem>
        )}

        <MenuItem
          onClick={handleExportarChatPdf}
          disabled={exportandoChatPdf || mensagens.filter((msg) => msg.tipo !== 'reaction').length === 0}
          sx={{ fontSize: '0.8rem' }}
        >
          {exportandoChatPdf ? (
            <CircularProgress size={16} sx={{ mr: 1 }} />
          ) : (
            <PdfIcon sx={{ fontSize: 16, mr: 1 }} />
          )}
          {exportandoChatPdf ? 'Exportando...' : 'Exportar PDF'}
        </MenuItem>

        {podeExcluirTicket && (
          <MenuItem onClick={abrirExclusaoTicket} sx={{ fontSize: '0.8rem', color: 'error.main' }}>
            <DeleteIcon sx={{ fontSize: 16, color: 'error.main', mr: 1 }} />
            Excluir Ticket
          </MenuItem>
        )}
        {atendimento.status !== 'fechado' && (
          <>
            <Divider />
            <MenuItem onClick={() => handleMudarStatus('fechado')} sx={{ fontSize: '0.8rem', fontWeight: 500, color: 'error.main' }}>
              <CloseIcon sx={{ fontSize: 16, mr: 1 }} />
              Finalizar Atendimento
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Popover Menu Figurinhas */}
      <Popover
        open={Boolean(anchorFigurinhas)}
        anchorEl={anchorFigurinhas}
        onClose={handleFecharFigurinhas}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 300,
            maxWidth: 'calc(100vw - 24px)',
            borderRadius: 2,
            boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
          }
        }}
      >
        <Box display="flex" alignItems="center" gap={0.7} mb={1}>
          <StickersIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            Figurinhas
          </Typography>
          {loadingFigurinhas && <CircularProgress size={13} sx={{ ml: 'auto' }} />}
        </Box>
        {!loadingFigurinhas && figurinhas.length === 0 ? (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', py: 1 }}>
            Nenhuma figurinha ativa encontrada.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 0.7,
              maxHeight: 280,
              overflowY: 'auto',
            }}
          >
            {figurinhas.map((figurinha) => (
              <IconButton
                key={figurinha.id}
                size="small"
                onClick={() => handleEnviarFigurinha(figurinha)}
                disabled={enviandoFigurinhaId !== null}
                sx={{
                  width: 62,
                  height: 62,
                  borderRadius: 1.5,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: 'action.hover',
                  p: 0.5,
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08), borderColor: 'primary.main' },
                }}
              >
                {enviandoFigurinhaId === figurinha.id ? (
                  <CircularProgress size={18} />
                ) : (
                  <Box
                    component="img"
                    src={getFigurinhaSrc(figurinha.caminho_arquivo)}
                    alt="Figurinha"
                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                )}
              </IconButton>
            ))}
          </Box>
        )}
      </Popover>

      {/* Popover Menu Emoji */}
      <Popover
        open={Boolean(anchorEmoji)}
        anchorEl={anchorEmoji}
        onClose={handleFecharEmojis}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 276,
            maxWidth: 'calc(100vw - 24px)',
            borderRadius: 2,
            boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
          }
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(8, 1fr)',
            gap: 0.35,
          }}
        >
          {EMOJIS_POPULARES.map((emoji, index) => (
            <IconButton
              key={`${emoji}-${index}`}
              size="small"
              onClick={() => handleSelecionarEmoji(emoji)}
              sx={{
                width: 30,
                height: 30,
                fontSize: '1.1rem',
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {emoji}
            </IconButton>
          ))}
        </Box>
      </Popover>

      <Dialog
        open={deleteTicketDialogOpen}
        onClose={() => {
          if (deletingTicket) return;
          setDeleteTicketDialogOpen(false);
        }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(4px)',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
            },
          },
        }}
      >
        <DialogTitle>Excluir Ticket</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja <strong>excluir permanentemente</strong> este ticket com <strong>{atendimento.contato.nome}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, color: 'error.main' }}>
            Esta acao nao pode ser desfeita. Todo o historico de mensagens sera apagado do banco de dados.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTicketDialogOpen(false)} disabled={deletingTicket}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarExclusaoTicket}
            color="error"
            variant="contained"
            disabled={deletingTicket}
          >
            {deletingTicket ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={transferDialogOpen}
        onClose={() => {
          if (transferindoTicket) return;
          setTransferDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(4px)',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
            },
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <TransferIcon color="primary" />
            Transferir Atendimento
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Escolha para onde transferir o atendimento de <strong>{atendimento.contato.nome}</strong>.
          </DialogContentText>

          <Box display="flex" gap={1} mb={2}>
            <Button
              fullWidth
              variant={transferTipo === 'usuario' ? 'contained' : 'outlined'}
              startIcon={<PersonIcon sx={{ fontSize: 16 }} />}
              onClick={() => {
                setTransferTipo('usuario');
                setTransferSetorId(atendimento.setor_id || atendimento.setor?.id || null);
              }}
              disabled={transferindoTicket}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Usuário
            </Button>
            <Button
              fullWidth
              variant={transferTipo === 'setor' ? 'contained' : 'outlined'}
              startIcon={<SetorIcon sx={{ fontSize: 16 }} />}
              onClick={() => {
                setTransferTipo('setor');
                setTransferUsuarioId(null);
              }}
              disabled={transferindoTicket}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Setor
            </Button>
          </Box>

          {transferTipo === 'usuario' ? (
            <UsuariosSelect
              value={transferUsuarioId}
              onChange={setTransferUsuarioId}
              label="Usuário de destino"
              placeholder="Escolha quem receberá o atendimento"
              disabled={transferindoTicket}
              allowClear
              size="small"
              helperText="O atendimento ficará em atendimento para o usuário selecionado."
            />
          ) : (
            <SetoresAllSelect
              value={transferSetorId}
              onChange={setTransferSetorId}
              disabled={transferindoTicket}
              label="Setor de destino"
              placeholder="Escolha o setor de destino"
            />
          )}

          <TextField
            fullWidth
            multiline
            minRows={3}
            value={transferMensagemPrivada}
            onChange={(event) => setTransferMensagemPrivada(event.target.value)}
            label="Nota privada (opcional)"
            placeholder="Mensagem interna sobre a transferência"
            disabled={transferindoTicket}
            sx={{ mt: 2 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            A nota privada aparece apenas no atendimento, nao sera enviada ao contato.
          </Typography>

          <Box sx={{ mt: 2, p: 1.25, borderRadius: 1.5, bgcolor: 'action.hover' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={sendTransferMessage}
                  onChange={(event) => setSendTransferMessage(event.target.checked)}
                  disabled={transferindoTicket}
                  color="primary"
                />
              }
              label="Enviar mensagem de transferencia"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
              Se ativo, envia o texto configurado quando a configuracao global tambem estiver ativa.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialogOpen(false)} disabled={transferindoTicket}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarTransferencia}
            variant="contained"
            startIcon={transferindoTicket ? <CircularProgress size={16} color="inherit" /> : <TransferIcon sx={{ fontSize: 16 }} />}
            disabled={transferindoTicket || (transferTipo === 'usuario' ? !transferUsuarioId : !transferSetorId)}
          >
            {transferindoTicket ? 'Transferindo...' : 'Transferir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assumeDialogOpen}
        onClose={() => {
          if (assumingTicket) return;
          setAssumeDialogOpen(false);
          setSendAssumeMessage(true);
        }}
        fullWidth
        maxWidth="xs"
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(4px)',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
            },
          },
        }}
      >
        <DialogTitle>Assumir Atendimento</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Deseja assumir este atendimento com <strong>{atendimento.contato.nome}</strong>?
          </DialogContentText>
          <SetoresAllSelect
            value={assumeSetorId}
            onChange={setAssumeSetorId}
            disabled={Boolean(atendimento.setor_id || atendimento.setor?.id) || assumingTicket}
            label="Setor"
            placeholder="Escolha o setor do atendimento"
          />
          {(atendimento.setor_id || atendimento.setor?.id) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Este atendimento ja possui setor atribuido.
            </Typography>
          )}
          {podeDesabilitarMsgAssumirTicket && (
            <Box sx={{ mt: 2, p: 1.25, borderRadius: 1.5, bgcolor: 'action.hover' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={sendAssumeMessage}
                    onChange={(event) => setSendAssumeMessage(event.target.checked)}
                    disabled={assumingTicket}
                    color="primary"
                  />
                }
                label="Enviar mensagem de boas-vindas ao aceitar"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
                Se ativo, envia o texto configurado quando a configuracao global tambem estiver ativa.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAssumeDialogOpen(false);
              setSendAssumeMessage(true);
            }}
            disabled={assumingTicket}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarAssumirTicket}
            color="success"
            variant="contained"
            disabled={assumingTicket || (!(atendimento.setor_id || atendimento.setor?.id) && !assumeSetorId)}
          >
            {assumingTicket ? 'Assumindo...' : 'Assumir Atendimento'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={closeDialogOpen}
        onClose={() => {
          if (closingTicket) return;
          setCloseDialogOpen(false);
          setSendFarewellMessage(true);
        }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(4px)',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
            },
          },
        }}
      >
        <DialogTitle>Encerrar Atendimento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja encerrar este atendimento com <strong>{atendimento.contato.nome}</strong>?
          </DialogContentText>
          {podeDesabilitarMsgFinalizarTicket && (
            <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, p: 1.5, mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={sendFarewellMessage}
                    onChange={(e) => setSendFarewellMessage(e.target.checked)}
                    disabled={closingTicket}
                    color="primary"
                  />
                }
                label="Enviar Mensagem de Encerramento"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
                Se ativo, envia o texto configurado quando a configuracao global tambem estiver ativa.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCloseDialogOpen(false);
              setSendFarewellMessage(true);
            }}
            disabled={closingTicket}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarEncerramento}
            color="error"
            variant="contained"
            disabled={closingTicket}
            sx={{
              transition: 'transform 0.2s ease',
              '&:hover': { transform: 'scale(1.05)' },
            }}
          >
            {closingTicket ? 'Finalizando...' : 'Encerrar Atendimento'}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={anchorMensagem}
        open={Boolean(anchorMensagem)}
        onClose={fecharMenuMensagem}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 150, py: 0.5 } }}
      >
        <MenuItem onClick={handleResponderMensagem} disabled={Boolean(mensagemSelecionada?.deletada)} sx={{ fontSize: '0.8rem', gap: 1 }}>
          <ReplyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          Responder
        </MenuItem>
        {mensagemSelecionada?.de_mim && (
          <MenuItem onClick={handleEditarMensagem} disabled={mensagemSelecionada?.tipo !== 'text' || Boolean(mensagemSelecionada?.deletada)} sx={{ fontSize: '0.8rem', gap: 1 }}>
            <EditIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            Editar
          </MenuItem>
        )}
        {mensagemSelecionada?.de_mim && (
          <MenuItem onClick={handleDeletarMensagem} disabled={Boolean(mensagemSelecionada?.deletada)} sx={{ fontSize: '0.8rem', gap: 1, color: 'error.main' }}>
            <DeleteIcon sx={{ fontSize: 16, color: 'error.main' }} />
            Deletar
          </MenuItem>
        )}
        {podeAdicionarFigurinha && mensagemSelecionada?.tipo === 'sticker' && !mensagemSelecionada?.deletada && (
          <MenuItem onClick={handleAbrirCadastroFigurinha} sx={{ fontSize: '0.8rem', gap: 1 }}>
            <StickersIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            Cadastrar figurinha
          </MenuItem>
        )}
      </Menu>

      <Modal open={Boolean(previewImage)} onClose={handleFecharImagem}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '92vw', sm: '82vw', md: 860 },
            maxWidth: 'calc(100vw - 32px)',
            height: { xs: '78vh', sm: '76vh' },
            maxHeight: 'calc(100vh - 64px)',
            bgcolor: 'background.paper',
            color: 'text.primary',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            boxShadow: '0 24px 72px rgba(0,0,0,0.35)',
            overflow: 'hidden',
            outline: 'none'
          }}
        >
          <Box
            sx={{
              height: 56,
              px: { xs: 1, sm: 2 },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
              borderBottom: `1px solid ${theme.palette.divider}`
            }}
          >
            <Typography variant="body2" noWrap sx={{ flex: 1, fontWeight: 600 }}>
              {previewImage?.name || 'Imagem'}
            </Typography>
            <Tooltip title="Diminuir zoom">
              <span>
                <IconButton size="small" onClick={() => setPreviewZoom((zoom) => Math.max(0.5, Number((zoom - 0.25).toFixed(2))))} disabled={previewZoom <= 0.5}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Redefinir zoom">
              <IconButton size="small" onClick={() => setPreviewZoom(1)}>
                <ResetZoomIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Aumentar zoom">
              <span>
                <IconButton size="small" onClick={() => setPreviewZoom((zoom) => Math.min(3, Number((zoom + 0.25).toFixed(2))))} disabled={previewZoom >= 3}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Baixar imagem">
              <IconButton size="small" onClick={handleDownloadImagem}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Fechar">
              <IconButton size="small" onClick={handleFecharImagem}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 1.5, sm: 2 },
              bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.default : 'grey.50'
            }}
          >
            {previewImage && (
              <Box
                component="img"
                src={previewImage.src}
                alt={previewImage.caption || previewImage.name || 'Imagem enviada'}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  transform: `scale(${previewZoom})`,
                  transformOrigin: 'center center',
                  transition: 'transform 0.15s ease',
                  borderRadius: 1,
                  boxShadow: '0 16px 48px rgba(0,0,0,0.35)'
                }}
              />
            )}
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={modalFotoAberto}
        onClose={() => setModalFotoAberto(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 0, bgcolor: 'black', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
          <IconButton
            onClick={() => setModalFotoAberto(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          {atendimento.contato.foto_perfil && (
            <img
              src={atendimentoService.getAvatarUrl(atendimento.contato.foto_perfil)}
              alt="Foto do Contato"
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={dialogCadastroFigurinhaOpen}
        onClose={() => {
          if (cadastrandoSticker) return;
          handleFecharCadastroFigurinha();
        }}
        maxWidth="xs"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(4px)',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
            },
          },
        }}
      >
        <DialogTitle>Cadastrar Figurinha</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {mensagemSelecionada?.media_url && (
            <Box
              component="img"
              src={getFigurinhaSrc(mensagemSelecionada.media_url)}
              alt="Preview"
              sx={{ width: '100%', height: 180, objectFit: 'contain', bgcolor: 'action.hover', borderRadius: 1, p: 1 }}
            />
          )}
          <FormControlLabel
            control={<Switch checked={novoStickerAtivo} onChange={(e) => setNovoStickerAtivo(e.target.checked)} color="success" />}
            label="Ativo"
          />
          <FormControlLabel
            control={<Switch checked={novoStickerCompartilhar} onChange={(e) => setNovoStickerCompartilhar(e.target.checked)} color="info" />}
            label="Compartilhar com todos"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleFecharCadastroFigurinha} disabled={cadastrandoSticker}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmarCadastroFigurinha}
            disabled={cadastrandoSticker || !mensagemSelecionada?.media_url}
          >
            {cadastrandoSticker ? <CircularProgress size={18} color="inherit" /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(arquivoColado)}
        onClose={() => !enviandoArquivoColado && handleFecharPreviewArquivoColado()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Enviar arquivo
          <IconButton
            onClick={handleFecharPreviewArquivoColado}
            size="small"
            disabled={enviandoArquivoColado}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            {arquivoColado?.type.startsWith('image/') && previewArquivoUrl && (
              <Box
                component="img"
                src={previewArquivoUrl}
                alt="Preview"
                sx={{ maxWidth: '100%', maxHeight: 300, borderRadius: 2, objectFit: 'contain' }}
              />
            )}
            {arquivoColado?.type.startsWith('video/') && previewArquivoUrl && (
              <Box
                component="video"
                src={previewArquivoUrl}
                controls
                sx={{ maxWidth: '100%', maxHeight: 300, borderRadius: 2 }}
              />
            )}
            {arquivoColado?.type.startsWith('audio/') && (
              <Box
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 2,
                }}
              >
                <MicIcon sx={{ fontSize: 48, color: 'success.main' }} />
                <Box sx={{ textAlign: 'center', width: '100%' }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    {arquivoColado.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {formatFileSize(arquivoColado.size)}
                  </Typography>
                  {previewArquivoUrl && (
                    <Box component="audio" controls sx={{ width: '100%', maxWidth: 400 }}>
                      <source src={previewArquivoUrl} type={arquivoColado.type} />
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            {arquivoColado && !arquivoColado.type.startsWith('image/') && !arquivoColado.type.startsWith('video/') && !arquivoColado.type.startsWith('audio/') && (
              <Box
                sx={{
                  p: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 2,
                }}
              >
                <DocumentIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {arquivoColado.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatFileSize(arquivoColado.size)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
          {arquivoColado && (arquivoColado.type.startsWith('image/') || arquivoColado.type.startsWith('video/')) && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {arquivoColado.name} • {formatFileSize(arquivoColado.size)}
            </Typography>
          )}
          <TextField
            fullWidth
            label="Legenda (opcional)"
            placeholder="Adicione uma legenda..."
            value={legendaArquivoColado}
            onChange={(e) => setLegendaArquivoColado(e.target.value)}
            multiline
            rows={2}
            disabled={enviandoArquivoColado}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleFecharPreviewArquivoColado} disabled={enviandoArquivoColado}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleEnviarArquivoColado}
            disabled={enviandoArquivoColado || !arquivoColado}
            startIcon={enviandoArquivoColado ? <CircularProgress size={20} /> : undefined}
          >
            {enviandoArquivoColado ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ViabilidadeModal
        open={viabilidadeModalOpen}
        onClose={() => {
          setViabilidadeModalOpen(false);
          setViabilidadeCoords(null);
        }}
        caixas={viabilidadeCaixas}
        loadingCaixas={loadingViabilidadeCaixas}
        onMostrarNoMapa={handleMostrarViabilidadeNoMapa}
        initialLat={viabilidadeCoords?.lat}
        initialLng={viabilidadeCoords?.lng}
        showMapActions
      />
    </Box>
  );
};

export default ChatColuna;

